// frontend/src/components/dashboard/ServicesDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../hooks/useSettings';

export default function ServicesDialog({ open, onClose, specialist, services, onSave }) {
  const { t } = useTranslation();
  const [selectedServices, setSelectedServices] = useState([]);
  const [error, setError] = useState('');
  const { settings, formatPrice } = useSettings();

  useEffect(() => {
      if (specialist) {
          setSelectedServices(specialist.services || []);
          setError('');
      }
  }, [specialist]);

  const groupedServices = services.reduce((acc, service) => {
      const category = service.category || t('services.uncategorized');
      if (!acc[category]) {
          acc[category] = [];
      }
      acc[category].push(service);
      return acc;
  }, {});

  const handleToggle = (serviceName) => {
      setSelectedServices(prev => {
          if (prev.includes(serviceName)) {
              return prev.filter(s => s !== serviceName);
          } else {
              return [...prev, serviceName];
          }
      });
      setError('');
  };

  const handleSave = async () => {
      try {
          await onSave(selectedServices);
          onClose();
      } catch (error) {
          setError(t('services.saveError'));
          console.error('Error saving services:', error);
      }
  };

  const handleSelectAll = () => {
      setSelectedServices(services.map(service => service.name));
      setError('');
  };

  const handleClearAll = () => {
      setSelectedServices([]);
      setError('');
  };

  return (
      <Dialog 
          open={open} 
          onClose={onClose} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
              sx: { minHeight: '50vh' }
          }}
      >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('common.editservicesfor')} {specialist?.name}
              <Tooltip title={t('common.selectservicesthisspecialistcanprovidetoclients')}>
                  <IconButton size="small">
                      <InfoIcon fontSize="small" />
                  </IconButton>
              </Tooltip>
          </DialogTitle>

          <DialogContent>
              {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                  </Alert>
              )}

              <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={handleSelectAll}>
                      {t('common.selectall')}
                  </Button>
                  <Button size="small" onClick={handleClearAll}>
                      {t('common.clearall')}
                  </Button>
              </Box>

              <FormGroup>
                  {Object.entries(groupedServices).map(([category, categoryServices]) => (
                      <Box key={category} sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                              {category}
                          </Typography>
                          {categoryServices.map((service) => (
                              <FormControlLabel
                                  key={service._id}
                                  control={
                                      <Checkbox
                                          checked={selectedServices.includes(service.name)}
                                          onChange={() => handleToggle(service.name)}
                                      />
                                  }
                                  label={
                                      <Box>
                                          <Typography variant="body1">
                                              {service.name}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                              {t('common.duration')}: {service.duration} {t('common.min')} | 
                                              {t('common.price')}: {
                                                  service.maxPrice ? 
                                                  `${formatPrice(service.price)} - ${formatPrice(service.maxPrice)}` :
                                                  formatPrice(service.price)
                                              }
                                          </Typography>
                                      </Box>
                                  }
                                  sx={{
                                      padding: '8px',
                                      borderRadius: 1,
                                      '&:hover': {
                                          backgroundColor: 'action.hover'
                                      }
                                  }}
                              />
                          ))}
                      </Box>
                  ))}
              </FormGroup>
          </DialogContent>

          <DialogActions sx={{ padding: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {selectedServices.length === 0 ? 
                      t('common.noServicesSelected') : 
                      `${t('common.selected')} ${selectedServices.length} ${t('common.services')}`
                  }
              </Typography>
              <Button onClick={onClose}>
                  {t('common.cancel')}
              </Button>
              <Button 
                  onClick={handleSave} 
                  variant="contained" 
                  color="primary"
              >
                  {t('common.saveservices')}
              </Button>
          </DialogActions>
      </Dialog>
  );
}